import React, { useEffect, useRef, useState } from 'react'
import { FileSearch, RefreshCw, X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { customerDisplayName } from '../lib/customerDirectory'

export default function CustomerFileAccessModal(props) {
  return <ScopedFileAccess key={`${props.workshopId}:${props.customer.id}`} {...props} />
}

function ScopedFileAccess({ workshopId, customer, onClose }) {
  const [files, setFiles] = useState([])
  const [review, setReview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const [basis, setBasis] = useState('original_delivery')
  const [note, setNote] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [preview, setPreview] = useState('')
  const active = useRef(false)
  const inFlight = useRef(false)
  const attempt = useRef(null)
  const controller = useRef(null)
  const previewUrl = useRef('')

  function clearPreview() {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current)
    previewUrl.current = ''
    setPreview('')
    setConfirmed(false)
  }

  async function request(body) {
    const { data } = await supabase.auth.getSession()
    if (!active.current) throw new Error('Review closed.')
    if (!data?.session?.access_token) throw new Error('Please sign in again.')
    controller.current = new AbortController()
    const response = await fetch('/.netlify/functions/customer-file-access', {
      method: 'POST', signal: controller.current.signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}` },
      body: JSON.stringify({ ...body, workshopId, customerId: customer.id }),
    })
    const payload = await response.json()
    if (!response.ok) throw new Error(payload.error || 'File review failed.')
    return payload
  }

  async function load() {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError('')
    try {
      const payload = await request({ action: 'list' })
      if (!active.current) return
      setFiles(payload.files || [])
      setReview(null)
      clearPreview()
      attempt.current = null
      setPending(false)
    } catch (err) { if (active.current) setError(err.message) }
    finally { if (active.current) { inFlight.current = false; setBusy(false) } }
  }

  useEffect(() => {
    active.current = true
    load()
    return () => { active.current = false; controller.current?.abort(); if (previewUrl.current) URL.revokeObjectURL(previewUrl.current) }
    // Scope changes remount this component and discard stale responses.
  }, [])

  async function decide(body) {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError('')
    attempt.current = attempt.current || body
    try {
      const payload = await request(attempt.current)
      if (!active.current) return
      if (payload.review?.workshop_id !== workshopId || payload.review?.customer_id !== customer.id
        || payload.review?.id !== attempt.current.reviewId
        || (attempt.current.action === 'prepare' && (payload.review.file_id !== attempt.current.fileId || payload.review.file_kind !== attempt.current.fileKind))) throw new Error('Review response did not match this customer and file.')
      if (attempt.current.action === 'prepare') {
        clearPreview()
        setReview(payload.review)
        setNote('')
        setConfirmed(false)
      } else {
        setFiles(current => current.map(file => file.id === payload.review.file_id && file.kind === payload.review.file_kind
          ? { ...file, review_id: payload.review.id, state: payload.review.state } : file))
        setReview(null)
        clearPreview()
      }
      attempt.current = null
      setPending(false)
    } catch (err) { if (active.current) { setPending(true); setError(err.message) } }
    finally { if (active.current) { inFlight.current = false; setBusy(false) } }
  }

  async function openOriginal() {
    if (inFlight.current || !review) return
    inFlight.current = true
    setBusy(true)
    setError('')
    const tab = window.open('about:blank', '_blank')
    if (tab) tab.opener = null
    try {
      if (!tab) throw new Error('Your browser blocked the original file. Allow the file window and retry.')
      const ref = review.context.reference
      const { data, error: downloadError } = await supabase.storage.from(ref.storage_bucket).download(ref.storage_path)
      if (downloadError || !data) throw new Error('Original file could not be opened using your workshop access.')
      const bytes = await data.arrayBuffer()
      const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), n => n.toString(16).padStart(2, '0')).join('')
      if (!active.current) { tab.close(); return }
      if (digest !== review.sha256) throw new Error('File changed since preparation. Reload and review again.')
      const extension = ref.file_name?.split('.').pop().toLowerCase()
      const mime = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif' }[extension]
      if (!mime) throw new Error('This file format requires a separate original-file review.')
      clearPreview()
      previewUrl.current = URL.createObjectURL(new Blob([bytes], { type: mime }))
      setPreview(previewUrl.current)
      tab.location.href = previewUrl.current
    } catch (err) { tab?.close(); if (active.current) setError(err.message) }
    finally { if (active.current) { inFlight.current = false; setBusy(false) } }
  }

  const locked = busy || pending
  return <div className="modal-overlay"><div className="modal" role="dialog" aria-modal="true" aria-labelledby="file-access-title" style={{ width: 'min(880px, 96vw)', maxHeight: '92vh', overflowY: 'auto' }}>
    <div className="modal-header"><h2 className="modal-title" id="file-access-title">Customer file access</h2>
      <button className="close-btn" aria-label="Close file access" onClick={onClose}><X size={20} /></button></div>
    <p><strong>{customerDisplayName(customer)}</strong></p>
    {error && <div className="alert alert-error" role="alert">{error}</div>}
    <div className="action-row" style={{ marginBottom: 16 }}>
      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={load} title="Reload current access status"><RefreshCw size={16} /> Reload status</button>
      {pending && <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => decide(attempt.current)}>Retry original action</button>}
    </div>
    {!files.length && <p className="text-muted" role="status">{busy ? 'Loading files...' : 'No original files need restoration.'}</p>}
    {files.map(file => <div key={`${file.kind}:${file.id}`} style={{ borderBottom: '1px solid var(--border)', padding: '12px 0', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
      <div style={{ flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere' }}><strong>{file.file_name}</strong><div className="text-muted fs12">{file.job_number} · {file.state === 'approved' ? 'Access approved' : file.state === 'revoked' ? 'Access revoked' : 'Needs review'}</div></div>
      {file.state === 'approved'
        ? <button className="btn btn-ghost btn-sm" disabled={locked} onClick={() => decide({ action: 'revoke', reviewId: file.review_id })}>Revoke access</button>
        : <button className="btn btn-ghost btn-sm" disabled={locked} onClick={() => decide({ action: 'prepare', fileKind: file.kind, fileId: file.id, reviewId: crypto.randomUUID() })}><FileSearch size={16} /> Review file</button>}
    </div>)}
    {review?.state === 'prepared' && <form onSubmit={event => { event.preventDefault(); if (preview && confirmed) decide({ action: 'approve', reviewId: review.id, basis, note, confirmed }) }} style={{ marginTop: 20 }}>
      <h3 style={{ fontSize: 16 }}>Review: {review.context.reference.file_name}</h3>
      <button type="button" className="btn btn-ghost btn-sm" disabled={locked} onClick={openOriginal}>Open original</button>
      {preview && <a href={preview} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }}>View original again</a>}
      <div className="form-group" style={{ marginTop: 16 }}><label htmlFor="file-proof-basis">Supporting record</label>
        <select id="file-proof-basis" value={basis} disabled={locked} onChange={e => setBasis(e.target.value)}><option value="original_delivery">Original delivered attachment</option><option value="independent_job_record">Independent original job record</option></select></div>
      <div className="form-group"><label htmlFor="file-proof-note">Record reference and customer match</label><textarea id="file-proof-note" value={note} disabled={locked} minLength={20} maxLength={2000} required rows={3} onChange={e => setNote(e.target.value)} /></div>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '16px 0', fontSize: 13, textTransform: 'none', letterSpacing: 0 }}><input type="checkbox" style={{ width: 18, height: 18, flex: '0 0 18px', marginTop: 2 }} checked={confirmed} disabled={locked || !preview} onChange={e => setConfirmed(e.target.checked)} />I verified this original belongs to {customerDisplayName(customer)} using the supporting record.</label>
      <button type="submit" className="btn btn-primary" disabled={locked || !preview || !confirmed || note.trim().length < 20}>Restore customer access</button>
    </form>}
  </div></div>
}
